import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { usePageContent, Paragraphs } from "@/lib/usePageContent";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight, Award, Clock, BookOpen, UserCheck } from "lucide-react";

const GUIDELINES = [
  "Assess originality, methodology, and significance objectively.",
  "Provide constructive, specific and courteous feedback to authors.",
  "Declare any conflicts of interest before accepting an assignment.",
  "Treat every manuscript as a confidential document.",
  "Return your review within the agreed timeframe (typically 2–3 weeks).",
  "Flag any suspected plagiarism or ethical concerns to the editor.",
];

const BENEFITS = [
  { icon: Award, title: "Recognition", text: "Build a verifiable record of your peer-review contributions." },
  { icon: BookOpen, title: "Stay Current", text: "Read cutting-edge research before it is published." },
  { icon: Clock, title: "Flexible", text: "Accept assignments that fit your expertise and schedule." },
];

export default function ForReviewers() {
  const c = usePageContent("for-reviewers");
  return (
    <div>
      <PageHeader eyebrow="For Reviewers" title={c?.title || "For Reviewers"} subtitle={c?.subtitle} breadcrumb="For Reviewers" />

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -top-5 -left-5 w-28 h-28 rounded-full bg-amber-400/30 blur-2xl hidden sm:block" />
            <img src="https://images.unsplash.com/photo-1581093577421-f561a654a353?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Researcher reviewing" className="relative rounded-2xl shadow-xl w-full h-[340px] object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="font-serif font-bold text-3xl text-slate-900 dark:text-slate-100">The role of a reviewer</h2>
            <div className="mt-4 space-y-4 text-slate-600 dark:text-slate-400 leading-relaxed">
              <Paragraphs text={c?.body} />
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              {BENEFITS.map((b) => (
                <div key={b.title} className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-4">
                  <b.icon className="w-6 h-6 text-primary dark:text-sky-400 mb-2" />
                  <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">{b.title}</h3>
                  <p className="text-xs text-slate-500 mt-1">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100 mb-5">Reviewer guidelines</h2>
            <ul className="space-y-3">
              {GUIDELINES.map((g) => (
                <li key={g} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> <span className="text-sm">{g}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative overflow-hidden rounded-2xl bg-primary text-white p-8 flex flex-col justify-center">
            <div className="absolute inset-0 hero-grid opacity-40" />
            <div className="relative">
              <UserCheck className="w-10 h-10 text-amber-400 mb-3" />
              <h2 className="font-serif font-bold text-2xl">Become a reviewer</h2>
              <p className="mt-3 text-slate-300 text-sm">Create an account and our editors will invite you to review submissions in your area of expertise. Already registered? Editorial staff can enable reviewer access on request.</p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Button asChild className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="reviewers-register-cta"><Link to="/register">Create an account <ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
                <Button asChild variant="outline" className="rounded-full border-white/30 bg-white/5 text-white hover:bg-white/15"><Link to="/contact">Contact the editors</Link></Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
